import { ThemeProvider } from '@front10/base-elements';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import { getUuid } from '@front10/helpers/dist/uuid';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import { EffectsMap, useEffectReducer } from 'use-effect-reducer';
import { isDayAfter, isDayBefore, isFutureOrPresentDay } from '../../../helpers/date';
import Box from '../../elements/Box';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import PopoverOld from '../Popover';
import { Popover } from './atoms/Popover';
import { useButton } from '@react-aria/button';
import { useIsMobileView } from '@front10/utils';
import Calendar from './atoms/Calendar';
import CalendarContent from './atoms/CalendarContent';
import CalendarFooter, { defaultFlexibleDates } from './atoms/CalendarFooter';
import CalendarHeader from './atoms/CalendarHeader';
import {
  formatDate,
  generateMonthList,
  getAddDays,
  isDateOutOfView,
  isDayBetween,
  isDayInRange,
  isSameDay,
  isValidDate,
  subMonthsNumber,
  transformBlackoutDaysToDate
} from './atoms/helper';
import { getRelativeDateLocalization } from '../../../helpers/localization/localizationUtils';
import { CalendarProvider, useEffectOnlyOnce, useGetHandleClickOutPopup } from './atoms/hooks';
import InputDateRangePicker from './atoms/InputDateRangePicker';
import WeekDayNames from './atoms/WeekDayNames';
import { dateRangeReducer } from './dateRangeReducer';
import {
  // CommonCalendarActions,
  CommonDateProps,
  DateModifier,
  DateRange,
  DayData,
  EffectSingleDateRangePicker,
  EventSingleDateRangePicker,
  StateSingleDateRangePicker,
  undefinedDate
} from './types';
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';

const NOT_AVALIABLES_DAYS = -1;
const DATE_OUT_OF_RANGE = 0;
const INCORRECT_FORMAT = 1;
const VALID_DATE = 2;

export interface OnChangeDataEvent {
  value?: Date;
  flexibleDateValue?: number | string;
}
interface ImperativeActions {
  clear: () => void;
}

//Defining State

//Defining props
export type SingleDateRangePickerProps = CommonDateProps & {
  /**
   * Calendar settings
   */
  calendarProps: any;
  /**
   * Default settings for both inputs
   */
  inputItem: any;
  /**
   * Default settings for left input
   */
  leftInputItem: any;
  /**
   * Default settings for right input
   */
  rightInputItem: any;

  /**
   * default value of date
   */
  defaultValue: DateRange;
  value: any;

  /**
   * onClear ranges
   */
  onClear: Function;
  /**
   * onChange function
   */
  onChange: Function;
  /*
   * onInputChange function
   */
  onInputChange: Function;

  /**
   * Patter to format date
   */
  focusReferences: Function;
  /**
   * function fired when focus the component
   */
  onFocus: (event: $TSFixMe, component: 'start' | 'end') => void;

  /**
   * Placeholder of the departure date
   */
  departInputPlaceholderLabelKey: string;
  /**
   * PLaceholder of the return date
   */
  returnInputPlaceholderLabelKey: string;
  /**
   * Close icon
   */
  iconClose: string;
  /**
   * Label key of input.
   */
  labelKey: string;
  /**
   * Label key of departure input.
   */
  departureLabelKey: string;
  /**
   * Label key of return input.
   */
  returnLabelKey: string;
  /**
   * Show label of input or not
   */
  showLabel: boolean;
  /**
   * Label Settings
   */
  textLabel: any;
  /**
   * clear button settings
   */
  clearButton: any;
  /*
   * navigationItem settings
   */
  navigationItem: any;
  showYearCaption: boolean;
  isMobile: boolean;
  border?: boolean;
  style: any;
  availableAtoms: Array<string>;
  showWeekDayNames?: boolean;
  setTopAtoms?: Array<string>;
  weekDaysContainer?: any;
  calendarContainer?: any;
  calendarMonthContainer?: any;
  callToAction?: any;
  topAtomsContainer?: any;
  footer: {
    elementsToShow: Array<string>;
    labelKeyInformation?: string;
  };
  departure: {
    start: number;
    end: number;
  };
  toggleElement: any;
  openPopupOnKeyDown?: boolean;
  openPopupOnClick?: boolean;
  isRequired?: boolean;
  isClearEmptyInput?: boolean;
  changePopoverState?: (popoverState: boolean) => void;
  showClearInputButton?: boolean;
  enabledExperimentalPopover?: boolean;
  momentParseStrictMode?: boolean;
  childrenRight?: any;
  childrenLeft?: any;
  minTripDuration?: number;
  maxTripDuration?: number;
  minDate?: Date;
  maxDate?: Date;
  dayWidthSetting?: {
    enableDynamicWidthCell?: boolean;
    width?: number;
  };
  datePickerDialogAriaLabel?: string;
};

const componentId = getUuid();

//const reducer = (state, action ) =>state;

function SingleDateRangePicker(
  {
    allowedCharacters = '[0-9A-Za-z ]',
    availableDays = 45,
    captionItem = {},
    calendarProps = {
      position: 'relative'
    },
    checkedFlexibleDates = false,
    showYearCaption = true,
    clearButton = {},
    departInputPlaceholderLabelKey = 'cmp-label--datepicker-depart',
    returnInputPlaceholderLabelKey = 'cmp-label--datepicker-return',
    enableFlexibleDates = false,
    flexibleDatesRangeValues = { ...defaultFlexibleDates },
    focusClass = '',
    footerCheckboxComponent = {},
    footerContainerComponent = {
      style: {
        display: 'flex',
        alignItem: 'center'
      }
    },
    openPopupOnClick = true,
    openPopupOnKeyDown = true,
    callToAction = {},
    footerSelectComponent = {},
    footerDoneButtonComponent = {},
    footerClearButtonComponent = {},
    showWeekDayNames = true,
    format = 'MM/DD/YYYY',
    focusReferences = () => {},
    iconClose = 'close',
    invalidClass = 'fr-input-invalid',
    inputDateScreenReader = 'MMMM D, YYYY',
    labelKey = 'cmp-date-range-selector-label',
    departureLabelKey = 'cmp-date-range-departure-label',
    returnLabelKey = 'cmp-date-range-return-label',
    footerInformationTextComponent,
    modifiersColors = {
      disabled: {
        'cmp-styles': {
          color: '#8c8c8c!important'
        }
      },
      beginRange: { bgColor: 'primary', textColor: 'white', padding: '2', roundedLeft: '1' },
      endRange: { bgColor: 'primary', textColor: 'white', padding: '2', roundedRight: '1' },
      selected: {},
      preSelected: { bgColor: 'custom', textColor: 'dark', padding: '2' },
      today: {
        fontWeight: 'bold'
      },
      past: {},
      blackout: {
        bgColor: 'white',
        'cmp-styles': {
          color: 'red!important'
        }
      }
    },
    navigationItem = { marginWidth: '1' },
    navColor = 'link',
    numberOfMonths: configuredNumberOfMonths = 2,
    onClear = () => {},
    onChange = () => {},
    onFocus = () => {},
    onInputChange = () => {},
    onValueClass = 'fr-value-selected',
    popover = {},
    renderWeekdayElement = null,
    renderNavbarElement = null,
    showInOneColumn = false,
    showLabel = false,
    showRemainingDays = false,
    leftInputItem = {},
    rightInputItem = {},
    border = false,
    inputItem = {
      closePopoverAfterAction: false,
      'cmp-styles': {
        '--point--fr-input-group': {
          'padding-top': '22px'
        }
      },
      rounded: '1',
      paddingRight: '0',
      border: true,
      showLabel: true,
      showLabelInside: false,
      textLabel: {
        paddingLeft: '2',
        marginTop: '2',
        position: 'absolute',
        top: '0',
        left: '0'
      },
      rightIcons: ['calendar-material'],
      leftButtonIconsProps: {
        paddingHeight: '0',
        color: 'transparent',
        marginRight: '0',
        paddingLeft: '2',
        border: false
      },
      rightButtonIconsProps: {
        paddingHeight: '0',
        color: 'transparent',
        paddingWidth: '2',
        border: false,
        'cmp-styles': {
          'margin-top': '-22px'
        }
      },
      leftIcons: [],
      leftIconsProps: {
        size: 20,
        style: null,
        viewBox: '0 0 24 24',
        'cmp-styles': {
          fill: 'colorTextSoft' //@palette:colorTextSoft
        }
      },
      rightIconsProps: {
        size: 20,
        style: null,
        viewBox: '0 0 24 24',
        'cmp-styles': {
          fill: 'colorTextSoft' //@palette:colorTextSoft
        }
      },
      leftTabIndex: [-1],
      inputSettings: {
        paddingLeft: '2',
        paddingTop: '0',
        paddingBottom: '0',
        paddingRight: '0px',
        textColor: 'colorText',
        'cmp-styles': {
          height: '30px',
          '@theme': 'eds.typo.fontSize5'
        }
      }
    },
    textLabel = {},
    rightIcon = {},
    rightIconOpen = {},
    defaultValue = undefinedDate,
    showSeparator = true,
    dayItems = {
      padding: '2'
    },
    weekdayItem = {
      weekDayFormat: 'oneLetter',
      fontWeight: 'bold',
      border: false
    },
    departure = {
      start: 0,
      end: 180
    },
    errorLabelComponent,
    blackoutDays = [],
    blackoutDaysFormat = format,
    availableAtoms = enableFlexibleDates
      ? ['CalendarHeader', 'Calendar', 'Footer']
      : ['CalendarHeader', 'Calendar'],
    footer = {
      elementsToShow: enableFlexibleDates ? ['FlexibleDates', 'DoneButton', 'CancelButton'] : [],
      labelKeyInformation: 'cmp-datePicker-text-information'
    },
    weekDaysContainer,
    calendarContainer,
    calendarMonthContainer,
    setTopAtoms = ['InputDate', 'WeekDayNames'],
    toggleElement,
    topAtomsContainer,
    changePopoverState,
    showClearInputButton = true,
    isRequired = false,
    isClearEmptyInput = false,
    enabledExperimentalPopover = false,
    momentParseStrictMode = true,
    childrenRight,
    childrenLeft,
    minTripDuration,
    maxTripDuration,
    minDate,
    maxDate,
    dayWidthSetting = {
      enableDynamicWidthCell: false,
      width: 39
    },
    datePickerDialogAriaLabel = 'cmp-date-picker-dialog-aria-label',
    ...props
  }: Partial<SingleDateRangePickerProps>,
  ref: Ref<ImperativeActions>
) {
  var isSameOrBefore = require('dayjs/plugin/isSameOrBefore');
  dayjs.extend(isSameOrBefore);
  const context = useContext(Context);
  const { translate, localizationData, moment: ingloriousMomentNeverUseMe } = context;
  // IMPORTANT:
  // When working with dates, the locale that must be imported
  // into dayjs or moment is the one returned by the localizationUtils,
  // as is the case for the fareItemLastSeen (FareItemLastSeen.jsx),
  // since it may have some modifications from the translations API.
  const relativeTime = getRelativeDateLocalization(translate, localizationData);
  dayjs.locale(relativeTime);
  if (
    relativeTime.name !== 'en' &&
    ingloriousMomentNeverUseMe &&
    typeof ingloriousMomentNeverUseMe.locale === 'function'
  ) {
    ingloriousMomentNeverUseMe.locale(relativeTime.name, relativeTime);
  }

  let isMobile = useIsMobileView();
  if (props.isMobile) {
    isMobile = true;
  }

  const departureUuid = getUuid();
  const returnUuid = getUuid();
  const buttonDepartureIconId = `date-picker-button-right-${departureUuid}`;
  const buttonDepartureClearId = `date-picker-clear-button-${departureUuid}`;
  const buttonReturnIconId = `date-picker-button-right-${returnUuid}`;
  const buttonReturnClearId = `date-picker-clear-button-${returnUuid}`;

  useEffect(() => {
    if (state.months[0] !== undefined) {
      const newDate = state.currentDate?.start
        ? state.currentDate.start
        : state.preSelected ?? state.months[0].firstAvailableDay() ?? dayjs().toDate();
      dispatch({
        type: 'SET_DISPLAY_DATE_ACTION',
        payload: newDate
      });
      dispatch({
        type: 'SET_PRESELECTED_DATE',
        payload: newDate
      });
      if (
        state.inputValue.start !== null &&
        state.inputValue.start !== undefined &&
        state.inputValue.start !== ''
      ) {
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: dayjs(newDate).format(format),
          component: 'start'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const initialState: StateSingleDateRangePicker = {
    selectedComponent: 'start',
    activeTab: 'exact',
    displayDate:
      minDate && dayjs(minDate).isValid() && dayjs(minDate).isAfter(dayjs(), 'day')
        ? dayjs(minDate).toDate()
        : dayjs().toDate(),
    availableDays: departure ? departure.start + departure.end : 0,
    months: [],
    today: dayjs().toDate(),
    currentDate: defaultValue ? defaultValue : null,
    preSelected: defaultValue ? defaultValue?.start : dayjs().toDate(),
    selectedRange: defaultValue,
    previousDate: null,
    popoverOpen: false,
    isCheckedFlexibleDates: checkedFlexibleDates,
    flexibleValue: flexibleDatesRangeValues?.defaultValue,
    inputValue: {
      start: defaultValue?.start
        ? formatDate(dayjs(defaultValue.start).toDate(), format, context)
        : undefined,
      end: defaultValue?.end
        ? formatDate(dayjs(defaultValue.end).toDate(), format, context)
        : undefined
    },
    valid: true,
    validEnd: true,
    typeError: VALID_DATE,
    typeErrorEnd: VALID_DATE,
    focusSelectedDay: false,
    enableFlexibleDates: enableFlexibleDates,
    blackoutDays: transformBlackoutDaysToDate(blackoutDays, blackoutDaysFormat, context),
    machineStatus: '',
    currentMonth: 0,
    currentMonthDateA11y:
      defaultValue && defaultValue.start ? dayjs(defaultValue.start).toDate() : undefined
  };
  const reduceEffectMap: EffectsMap<
    StateSingleDateRangePicker,
    EventSingleDateRangePicker,
    EffectSingleDateRangePicker
  > = {
    RESET_ACTION_AND_CLEAR_STATE: (state) => {
      const { flexibleValue: stateFlexibleDateValue, isCheckedFlexibleDates } = state;
      const mergedFlexibleValue = isCheckedFlexibleDates ? stateFlexibleDateValue : 0;
      if (props?.value?.start || props?.value?.end) {
        onChange({
          start: undefined,
          end: undefined,
          flexibleDates: mergedFlexibleValue,
          availableDays: state.availableDays || NOT_AVALIABLES_DAYS,
          enabledFlexibleDates: enableFlexibleDates,
          checkedFlexibleDates: isCheckedFlexibleDates
        });
      }
    },
    CLEAR_ACTION_DEPARTURE: (state) => {
      const {
        flexibleValue: stateFlexibleDateValue,
        isCheckedFlexibleDates,
        selectedRange: stateValue
      } = state;
      const mergedFlexibleValue = isCheckedFlexibleDates ? stateFlexibleDateValue : 0;
      onChange({
        start: undefined,
        end: stateValue?.end ? stateValue?.end : undefined,
        flexibleDates: mergedFlexibleValue,
        availableDays: state.availableDays || NOT_AVALIABLES_DAYS,
        enabledFlexibleDates: enableFlexibleDates,
        checkedFlexibleDates: isCheckedFlexibleDates
      });
    },
    CLEAR_ACTION_RETURN: (state) => {
      const {
        flexibleValue: stateFlexibleDateValue,
        isCheckedFlexibleDates,
        selectedRange: stateValue
      } = state;
      const mergedFlexibleValue = isCheckedFlexibleDates ? stateFlexibleDateValue : 0;
      onChange({
        start: stateValue?.start ? stateValue?.start : undefined,
        end: undefined,
        flexibleDates: mergedFlexibleValue,
        availableDays: state.availableDays || NOT_AVALIABLES_DAYS,
        enabledFlexibleDates: enableFlexibleDates,
        checkedFlexibleDates: isCheckedFlexibleDates
      });
    },
    SET_CURRENT_DATE: (state) => {},
    RESET_DATA_ACTION: (state) => {},
    SET_CURRENT_MONTH_DATE_A11Y: (state) => {},
    CHANGE_CURRENT_MONTH: (state) => {},
    SET_DISPLAY_DATE_ACTION: (state) => {
      let lastDate = getAddDays(
        state.today,
        departure?.end ? departure?.start + departure?.end - 1 : 0
      );
      if (maxDate) {
        if (state.selectedComponent === 'start') {
          lastDate = dayjs(maxDate).toDate();
        } else {
          if (maxTripDuration && maxTripDuration > 0) {
            lastDate = getAddDays(
              state.selectedRange.start ? state.selectedRange.start : state.today,
              maxTripDuration
            );
          } else {
            lastDate = dayjs(maxDate).toDate();
          }
        }
      }
      const isEnabledNextMonth = isDateOutOfView(state.displayDate, numberOfMonths, lastDate);
      const months = generateMonthList({
        displayDate: isEnabledNextMonth
          ? state.displayDate
          : !isMobile
          ? dayjs(state.displayDate).get('month') === dayjs(lastDate).get('month') &&
            numberOfMonths === 1
            ? state.displayDate
            : subMonthsNumber(state.displayDate, 1)
          : state.displayDate,
        modifiers,
        modifiersColors,
        numberOfMonths: numberOfMonths,
        blackOutDays: state.blackoutDays,
        showRemainingDays,
        locale: localizationData
      });

      dispatch({ type: 'SET_MONTHS', payload: months });
      if (state.currentMonth) {
        let newCurrentMonth = state.currentMonth + 1;
        if (state.currentMonth < 0 && newCurrentMonth > 0) {
          newCurrentMonth = -newCurrentMonth;
        } else if (state.currentMonth < 0 && newCurrentMonth === 0) {
          newCurrentMonth = state.currentMonth - 1;
        }
        dispatch({
          type: 'CHANGE_CURRENT_MONTH',
          payload: newCurrentMonth
        });
      }

      if (!isMobile && !isEnabledNextMonth && numberOfMonths > 1) {
        dispatch({
          type: 'SET_DISPLAY_DATE_ACTION',
          payload: subMonthsNumber(state.displayDate, 1)
        });
      }
    },
    SET_DATE_ACTION: (state, action) => {
      if (state.currentDate?.start || state.currentDate?.end) {
        throwOnChangeEvent();
      }
      if (action.close) {
        if (enabledExperimentalPopover) {
          overlayState.close();
        } else {
          popoverRef?.current?.closePopover();
        }
      }
    },
    CHANGE_ACTION: (state) => {
      handleClickOutPopup(state, dispatch);
      if (state.currentDate?.start || state.currentDate?.end) {
        throwOnChangeEvent();
      }
    },
    CLEAR_ACTION: (state) => {
      if (!state.enableFlexibleDates) {
        throwOnChangeEvent();
      }
      leftInputRef?.current?.focus();
      onClear();
    },
    RESET_ACTION: (state) => {},
    CANCEL_ACTION: (state) => {
      if (state.currentDate?.start) {
        dispatch({ type: 'SET_PRESELECTED_DATE', payload: state.previousDate?.start });
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: state.currentDate?.start ? dayjs(state.currentDate.start).format(format) : '',
          component: 'start'
        });
      }
      if (enabledExperimentalPopover) {
        overlayState.close();
      } else {
        popoverRef?.current?.closePopover();
      }
    },
    UNDO_ACTION: (state) => {
      if (state.currentDate?.start) {
        dispatch({ type: 'SET_PRESELECTED_DATE', payload: state.currentDate?.start });
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: state.currentDate?.start ? dayjs(state.currentDate.start).format(format) : '',
          component: 'start'
        });
      }
      if (state.currentDate?.end) {
        dispatch({ type: 'SET_PRESELECTED_DATE', payload: state.currentDate?.end });
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: state.currentDate?.end ? dayjs(state.currentDate.end).format(format) : '',
          component: 'end'
        });
      }
      if (enabledExperimentalPopover) {
        overlayState.close();
      } else {
        popoverRef?.current?.closePopover();
      }
    },
    SET_PRESELECTED_DATE: (state, action) => {
      dispatch({
        type: 'SET_INPUT_VALUE',
        payload: dayjs(action?.payload).format(format),
        component: state.selectedComponent
      });
    },
    SELECT_DAY_ACTION: (state, action) => {
      if (state.selectedComponent === 'start') {
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: dayjs(action.payload.start).format(format),
          component: 'start'
        });
        if (!Boolean(state.selectedRange.end)) {
          dispatch({
            type: 'SET_INPUT_VALUE',
            payload: '',
            component: 'end'
          });
        }

        if (action.payload.start && maxTripDuration && maxTripDuration > 0) {
          if (!isMobile) {
            dispatch({
              type: 'SET_DISPLAY_DATE_ACTION',
              payload: action.payload.start
            });
          }

          if (
            state.selectedRange.end &&
            isDayBefore(getAddDays(action.payload.start, maxTripDuration), state.selectedRange.end)
          ) {
            dispatch({
              type: 'SELECT_DAY_ACTION',
              payload: { end: getAddDays(action.payload.start, maxTripDuration) }
            });
          }

          if (
            action.payload.start &&
            minTripDuration &&
            minTripDuration > 0 &&
            isDayAfter(getAddDays(action.payload.start, minTripDuration), state.selectedRange.end)
          ) {
            dispatch({
              type: 'SELECT_DAY_ACTION',
              payload: { end: getAddDays(action.payload.start, minTripDuration) }
            });
          }
        }
        if (minTripDuration && minTripDuration > 0) {
          dispatch({
            type: 'MIN_TRIP_DURATION_DAYS',
            payload: [
              {
                start: dayjs(state.today).toDate(),
                end: dayjs(action.payload.start)
                  .add(minTripDuration - 1, 'd')
                  .toDate()
              }
            ]
          });
        }
      } else {
        if (Boolean(state.selectedRange.end)) {
          dispatch({
            type: 'SET_INPUT_VALUE',
            payload: dayjs(action.payload.end).format(format),
            component: 'end'
          });
          if (state.selectedRange.start === state.selectedRange.end) {
            dispatch({
              type: 'SET_INPUT_VALUE',
              payload: dayjs(action.payload.end).format(format),
              component: 'start'
            });
          }
        } else {
          dispatch({
            type: 'SET_INPUT_VALUE',
            payload: dayjs(action.payload.start).format(format),
            component: 'start'
          });
        }
        if (isMobile && maxTripDuration && maxTripDuration > 0) {
          dispatch({
            type: 'SET_DISPLAY_DATE_ACTION',
            payload:
              minDate && dayjs(minDate).isValid() && dayjs(minDate).isAfter(dayjs(), 'day')
                ? dayjs(minDate).toDate()
                : new Date()
          });
        }
      }

      if (!enableFlexibleDates && footer.elementsToShow.length === 0 && !isMobile) {
        dispatch({ type: 'CHANGE_ACTION' });
      }
    },

    PARSE_INPUT_ACTION: (state, action) => {
      dayjs.extend(customParseFormat);

      let valueStart = state.inputValue.start;
      valueStart = valueStart?.toLowerCase();
      if (valueStart && valueStart?.charAt(0) >= 'a' && valueStart?.charAt(0) <= 'z') {
        valueStart =
          valueStart?.charAt(0).toUpperCase() + valueStart.substring(1, valueStart.length);
      }

      const isCorrectFormatStart = ingloriousMomentNeverUseMe(
        valueStart,
        format,
        momentParseStrictMode
      ).isValid();
      const dateStart = ingloriousMomentNeverUseMe(valueStart, format).toDate();

      let valueEnd = state.inputValue.end;
      valueEnd = valueEnd?.toLowerCase();
      if (valueEnd && valueEnd?.charAt(0) >= 'a' && valueEnd?.charAt(0) <= 'z') {
        valueEnd = valueEnd?.charAt(0).toUpperCase() + valueEnd.substring(1, valueEnd.length);
      }

      const isCorrectFormatEnd = ingloriousMomentNeverUseMe(
        valueEnd,
        format,
        momentParseStrictMode
      ).isValid();
      const dateEnd = ingloriousMomentNeverUseMe(valueEnd, format).toDate();

      let validateStart = false;
      let validateEnd = false;

      if (valueStart) {
        validateStart = validate(dateStart, isCorrectFormatStart, 'start');
      }
      if (valueEnd) {
        validateEnd = validate(dateEnd, isCorrectFormatEnd, 'end', dateStart);
      }

      if (validateStart && validateEnd) {
        if (action.component === 'start') {
          if (!dayjs(dateEnd).isSameOrAfter(dayjs(dateStart), 'day')) {
            dispatch({
              type: 'SET_TYPE_ERROR',
              payload: DATE_OUT_OF_RANGE
            });
            dispatch({ type: 'SET_VALID', payload: false });
          } else {
            setDateValid(dateStart, 'start');
            setDateValid(dateEnd, 'end');
          }
        } else {
          if (!dayjs(dateStart).isSameOrBefore(dayjs(dateEnd), 'day')) {
            dispatch({
              type: 'SET_TYPE_ERROR_END',
              payload: DATE_OUT_OF_RANGE
            });
            dispatch({ type: 'SET_VALID_END', payload: false });
          } else if (minTripDuration && minTripDuration > 0) {
            if (!(dayjs(dateEnd).diff(dayjs(dateStart), 'day') >= minTripDuration)) {
              dispatch({
                type: 'SET_TYPE_ERROR_END',
                payload: DATE_OUT_OF_RANGE
              });
              dispatch({ type: 'SET_VALID_END', payload: false });
            } else {
              setDateValid(dateStart, 'start');
              setDateValid(dateEnd, 'end');
            }
          } else if (maxTripDuration && maxTripDuration > 0) {
            if (!(dayjs(dateEnd).diff(dayjs(dateStart), 'day') <= maxTripDuration)) {
              dispatch({
                type: 'SET_TYPE_ERROR_END',
                payload: DATE_OUT_OF_RANGE
              });
              dispatch({ type: 'SET_VALID_END', payload: false });
            } else {
              setDateValid(dateStart, 'start');
              setDateValid(dateEnd, 'end');
            }
          } else {
            setDateValid(dateStart, 'start');
            setDateValid(dateEnd, 'end');
          }
        }
        return;
      } else if (validateStart && valueEnd === '') {
        setDateValid(dateStart, 'start');
        return;
      } else if (validateEnd && valueStart === '') {
        setDateValid(dateEnd, 'end');
        setDateValid(dateEnd, 'start');
        return;
      } else if (!validateStart) {
        if (!isCorrectFormatStart) {
          dispatch({ type: 'SET_TYPE_ERROR', payload: INCORRECT_FORMAT });
        } else if (!isFutureOrPresentDay(dateStart) || !isValidDate(dateStart, modifiers)) {
          dispatch({ type: 'SET_TYPE_ERROR', payload: DATE_OUT_OF_RANGE });
        }
      } else if (!validateEnd) {
        if (!isCorrectFormatEnd) {
          dispatch({ type: 'SET_TYPE_ERROR', payload: INCORRECT_FORMAT });
        } else if (!isFutureOrPresentDay(dateEnd) || !isValidDate(dateEnd, modifiers)) {
          dispatch({ type: 'SET_TYPE_ERROR', payload: DATE_OUT_OF_RANGE });
        }
      }
    },
    CLOSE_POPOVER_ACTION: () => {
      if (enabledExperimentalPopover) {
        overlayState.close();
      } else {
        popoverRef?.current?.closePopover();
      }
    },
    OPEN_POPOVER_ACTION: () => {
      if (enabledExperimentalPopover) {
        if (!isCloseRef.current) {
          overlayState.open();
        }
        isCloseRef.current = false;
      } else {
        popoverRef?.current?.openPopover();
      }
    },
    SET_TRIP_DURATION: () => {},
    SET_ACTIVE_TAB: () => {},
    SELECT_MONTH_RANGE: () => {}
  };
  const setDateValid = (date: Date, component: 'start' | 'end') => {
    if (state.validateDone) {
      dispatch({
        type: 'SET_PRESELECTED_DATE',
        payload: date
      });
      dispatch({ type: 'VALIDATE_DONE' });
    } else {
      dispatch({
        type: 'SET_DATE_ACTION',
        payload: date,
        component: component,
        close: true
      });
    }
    dispatch({
      type: component === 'start' ? 'SET_TYPE_ERROR' : 'SET_TYPE_ERROR_END',
      payload: VALID_DATE
    });
  };

  const validate = (
    date: Date,
    isCorrectFormat: Boolean,
    component: 'start' | 'end',
    dateStart?: Date | undefined
  ) => {
    let mod = { ...modifiers };
    if (component === 'end' && maxTripDuration && maxTripDuration > 0 && dateStart) {
      mod = {
        ...modifiers,
        disabled: {
          before: dateStart,
          after: getAddDays(dateStart, maxTripDuration)
        }
      };
    }

    if (isCorrectFormat && !!date && isFutureOrPresentDay(date) && isValidDate(date, mod)) {
      return true;
    } else {
      if (!isCorrectFormat) {
        dispatch({
          type: component === 'start' ? 'SET_TYPE_ERROR' : 'SET_TYPE_ERROR_END',
          payload: INCORRECT_FORMAT
        });
      } else if (!isFutureOrPresentDay(date) || !isValidDate(date, modifiers)) {
        dispatch({
          type: component === 'start' ? 'SET_TYPE_ERROR' : 'SET_TYPE_ERROR_END',
          payload: DATE_OUT_OF_RANGE
        });
      }
    }

    if (component === 'start') {
      dispatch({ type: 'SET_VALID', payload: false });
    } else {
      dispatch({ type: 'SET_VALID_END', payload: false });
    }

    if (state.validateDone) {
      dispatch({ type: 'VALIDATE_DONE' });
    }
    return false;
  };

  const [state, dispatch] = useEffectReducer<
    StateSingleDateRangePicker,
    EventSingleDateRangePicker,
    EffectSingleDateRangePicker
  >(dateRangeReducer, initialState, reduceEffectMap);

  //refs
  const popoverRef = useRef<any>(null);
  const leftInputRef = useRef<HTMLElement>(null);
  const rightInputRef = useRef<HTMLElement>(null);
  const leftComponentReference = useRef<HTMLElement>(null);
  const rightComponentReference = useRef<HTMLElement>(null);
  const flexibleFilterContainerRef = useRef<any>(null);
  const containerRef = useRef<any>(null);
  const isCloseRef = useRef<any>(false);
  const [accessibilityState, setAccessibilityState] = React.useState(false);

  const { theme, direction } = context;
  const newProps = { ...props, border };
  const [className, style] = useStyles(newProps, { theme, direction });

  const numberOfMonths = useMemo(() => {
    if (departure?.start != null && isMobile) {
      let startDate =
        minDate && dayjs(minDate).isAfter(dayjs(), 'day')
          ? dayjs(minDate)
          : dayjs().add(departure.start, 'day');
      let endDate = maxDate
        ? dayjs(maxDate)
        : dayjs().add(departure.start + departure.end - 1, 'day');

      if (
        state.selectedComponent === 'end' &&
        state.selectedRange.start &&
        maxTripDuration &&
        isDayBefore(dayjs(maxDate).toDate(), getAddDays(state.selectedRange.start, maxTripDuration))
      ) {
        endDate = dayjs(state.selectedRange.start).add(maxTripDuration, 'day');
      }

      let monthDiff = endDate.diff(startDate, 'month');

      if (monthDiff === 0) {
        return configuredNumberOfMonths;
      }
      monthDiff = 0;
      const initialDate = dayjs(new Date(startDate.get('year'), startDate.get('month'), 1));
      let tmpEndDate = initialDate;
      while (tmpEndDate.isSameOrBefore(endDate, 'day')) {
        monthDiff++;
        tmpEndDate = initialDate.add(monthDiff, 'month');
      }
      return monthDiff;
    } else {
      return configuredNumberOfMonths;
    }
  }, [
    departure.start,
    departure.end,
    isMobile,
    minDate,
    maxDate,
    state.selectedComponent,
    state.selectedRange.start,
    maxTripDuration,
    configuredNumberOfMonths
  ]);

  useEffectOnlyOnce(() => {
    dispatch({
      type: 'SET_DISPLAY_DATE_ACTION',
      payload:
        minDate && dayjs(minDate).isValid() && dayjs(minDate).isAfter(dayjs(), 'day')
          ? dayjs(minDate).toDate()
          : new Date()
    });
  }, [dispatch]);

  useEffect(() => {
    if (minTripDuration && minTripDuration > 0 && state.selectedComponent === 'start') {
      dispatch({
        type: 'MIN_TRIP_DURATION_DAYS',
        payload: []
      });
    }
  }, [dispatch, minTripDuration, state.selectedComponent]);

  //set modifiers to be used by datePicker
  const modifiers: Partial<DateModifier> = {
    today: (day: Date) => isSameDay(day, state.today),
    beginRange: (day: Date) => {
      return state.selectedRange?.start ? isSameDay(day, state.selectedRange?.start) : false;
    },
    endRange: (day) =>
      state.selectedRange?.end ? isSameDay(day, state.selectedRange?.end) : false,
    selected: (day) =>
      isDayInRange(day, { start: state.selectedRange?.start, end: state.selectedRange?.end }),
    past: (day) => isDayBefore(day, state.today),
    disabled: {
      before:
        maxTripDuration &&
        maxTripDuration > 0 &&
        state.selectedRange.start &&
        state.selectedComponent === 'end'
          ? state.selectedRange.start
          : minDate
          ? isDayBefore(dayjs(minDate).toDate(), state.today)
            ? state.today
            : dayjs(minDate).toDate()
          : departure && departure.start
          ? dayjs(state.today).add(departure.start, 'day').toDate()
          : state.today,
      after:
        maxTripDuration &&
        maxTripDuration > 0 &&
        state.selectedRange.start &&
        state.selectedComponent === 'end'
          ? getAddDays(state.selectedRange.start, maxTripDuration)
          : maxDate
          ? dayjs(maxDate).toDate()
          : departure && departure.end && departure.start
          ? dayjs(state.today)
              .add(departure.end + departure.start - 1, 'day')
              .toDate()
          : getAddDays(state.today, state.availableDays - 1)
    },
    blackout: (date) => {
      return state.blackoutDays.some((current: DateRange) => {
        if (current.start && current.end) {
          return isDayBetween(date, current?.start, current?.end, true);
        }
        return false;
      });
    },
    preSelected: (day) => isSameDay(day, state.preSelected)
  };

  //input events

  const handleInputChange = (event: any, component: 'start' | 'end') => {
    dispatch({ type: 'SET_INPUT_VALUE', payload: event.target.value, component });
    onInputChange(event);
  };

  const handleDayClick = (dayData: DayData) => {
    // console.log('selecting a date', dayData);

    dispatch({ type: 'SET_PRESELECTED_DATE', payload: dayData.date });
    if (state.selectedComponent === 'start' && isMobile && maxTripDuration && maxTripDuration > 0) {
      dispatch({
        type: 'SET_DISPLAY_DATE_ACTION',
        payload:
          minDate && dayjs(minDate).isAfter(dayjs(), 'day')
            ? dayjs(minDate).toDate()
            : dayjs().add(departure.start, 'day').toDate()
      });
    }

    if (state.selectedComponent === 'start') {
      if (
        Boolean(state.selectedRange.start) &&
        Boolean(state.selectedRange.end) &&
        isDayAfter(dayData.date, state.selectedRange.end)
      ) {
        dispatch({
          type: 'SELECT_DAY_ACTION',
          payload: { end: undefined }
        });
      }
      dispatch({
        type: 'SELECT_DAY_ACTION',
        payload: { start: dayData.date }
      });
    } else if (state.selectedComponent === 'end') {
      if (state.preSelected) {
        if (state.selectedRange === null || Object.keys(state.selectedRange).length === 0) {
          dispatch({
            type: 'SET_DATE_ACTION',
            payload: state.preSelected,
            component: 'start',
            close: false
          });
          dispatch({
            type: 'SET_DATE_ACTION',
            payload: state.preSelected,
            component: state.selectedComponent,
            close: true
          });
        }
      }
      if (
        Boolean(state.selectedRange.start) &&
        isDayBefore(dayData.date, state.selectedRange.start)
      ) {
        dispatch({
          type: 'SELECT_DAY_ACTION',
          payload: { start: dayData.date, end: dayData.date }
        });
      } else {
        if (!Boolean(state.selectedRange.start)) {
          dispatch({
            type: 'SELECT_DAY_ACTION',
            payload: {
              start:
                minTripDuration && minTripDuration > 0
                  ? dayjs(dayData.date).subtract(minTripDuration, 'd').toDate()
                  : dayData.date
            }
          });
        }
        dispatch({
          type: 'SELECT_DAY_ACTION',
          payload: { end: dayData.date }
        });
      }
    }
    if (state.selectedComponent === 'start') {
      isMobile && dispatch({ type: 'SET_SELECTED_INPUT', payload: 'end' });
      if (state.selectedRange.end && !isMobile) {
        dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: state.selectedRange.end });
      }
      rightInputRef.current?.focus();
    } else {
      leftInputRef.current?.focus();
      isMobile && dispatch({ type: 'SET_SELECTED_INPUT', payload: 'start' });
      if (state.selectedRange.start && !isMobile) {
        dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: state.selectedRange.start });
      }
    }
  };

  //popover events
  const handleClosePopover = () => {
    changePopoverState?.(false);
    dispatch({ type: 'SET_POPOVER', payload: { popoverOpen: false, focusSelectedDay: false } });

    dispatch({
      type: 'SET_PRESELECTED_DATE',
      payload: state.selectedRange?.start ? state.selectedRange.start : undefined
    });
    dispatch({
      type: 'SET_VALID_END',
      payload: true
    });
    dispatch({
      type: 'SET_INPUT_VALUE',
      payload: state.selectedRange?.start ? dayjs(state.selectedRange.start).format(format) : '',
      component: 'start'
    });
    dispatch({
      type: 'SET_INPUT_VALUE',
      payload: state.selectedRange?.end ? dayjs(state.selectedRange.end).format(format) : '',
      component: 'end'
    });

    dispatch({
      type: 'SET_USER_INTERACTION_ACTION',
      payload: false
    });

    if (state.selectedComponent === 'start') {
      leftInputRef?.current?.focus();
    } else {
      rightInputRef?.current?.focus();
    }
  };

  const [isOpenPopover, setIsOpenPopover] = React.useState(false);

  const handleOpenPopover = () => {
    changePopoverState?.(false);
    dispatch({ type: 'SET_POPOVER', payload: { popoverOpen: true, focusSelectedDay: false } });
    dispatch({
      type: 'SET_USER_INTERACTION_ACTION',
      payload: false
    });
    if (state.currentDate?.start !== state.selectedRange.start) {
      dispatch({
        type: 'SET_DATE_ACTION',
        payload: state.selectedRange?.start ? state.selectedRange.start : null,
        component: 'start',
        close: false
      });
    }
    if (state.currentDate?.end !== state.selectedRange.end) {
      dispatch({
        type: 'SET_DATE_ACTION',
        payload: state.selectedRange?.end ? state.selectedRange.end : null,
        component: 'end',
        close: false
      });
    }

    if (minTripDuration && minTripDuration > 0 && state.selectedComponent === 'end') {
      if (state.selectedRange?.start) {
        dispatch({
          type: 'MIN_TRIP_DURATION_DAYS',
          payload: [
            {
              start: dayjs(state.today).toDate(),
              end: dayjs(state.selectedRange?.start)
                .add(minTripDuration - 1, 'd')
                .toDate()
            }
          ]
        });
      } else {
        dispatch({
          type: 'MIN_TRIP_DURATION_DAYS',
          payload: [
            {
              start: isDayBefore(dayjs(minDate).toDate(), state.today)
                ? state.today
                : dayjs(minDate).toDate(),

              end: dayjs(
                isDayBefore(dayjs(minDate).toDate(), state.today)
                  ? state.today
                  : dayjs(minDate).toDate()
              )
                .add(minTripDuration - 1, 'd')
                .toDate()
            }
          ]
        });
      }
    }
    //  else {
    //   if (minTripDuration && state.selectedComponent === 'start') {
    //     // if (maxDate) {
    //     //   dispatch({
    //     //     type: 'MIN_TRIP_DURATION_DAYS',
    //     //     payload: [
    //     //       {
    //     //         start: dayjs(maxDate)
    //     //           .subtract(minTripDuration - 1, 'd')
    //     //           .toDate(),
    //     //         end: dayjs(maxDate).toDate()
    //     //       }
    //     //     ]
    //     //   });
    //     // } else {
    //     // dispatch({
    //     //   type: 'MIN_TRIP_DURATION_DAYS',
    //     //   payload: [
    //     //     {
    //     //       start: dayjs().add(departure.end, 'day').subtract(minTripDuration, 'd').toDate(),
    //     //       end: dayjs().add(departure.end, 'day').toDate()
    //     //     }
    //     //   ]
    //     // });
    //     // }
    //   } else {
    //     dispatch({
    //       type: 'MIN_TRIP_DURATION_DAYS',
    //       payload: []
    //     });
    //   }
    // }
  };
  //component events
  const throwOnChangeEvent = ({ value, flexibleDateValue = 0 }: OnChangeDataEvent = {}) => {
    const {
      selectedRange: stateValue,
      flexibleValue: stateFlexibleDateValue,
      isCheckedFlexibleDates
    } = state;

    const mergedValue = value ? value : stateValue?.start;
    const mergedFlexibleValue = isCheckedFlexibleDates
      ? flexibleDateValue || stateFlexibleDateValue
      : 0;

    onChange({
      start: mergedValue,
      end: stateValue?.end ? stateValue?.end : undefined,
      flexibleDates: mergedFlexibleValue,
      availableDays: state.availableDays || NOT_AVALIABLES_DAYS,
      enabledFlexibleDates: enableFlexibleDates,
      checkedFlexibleDates: isCheckedFlexibleDates
    });
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      dispatch({ type: 'CLEAR_ACTION' });
    },
    reset: () => {
      dispatch({ type: 'RESET_DATA_ACTION' });
    },
    validate: () => {
      dispatch({
        type: 'MIN_TRIP_DURATION_DAYS',
        payload: []
      });
      dispatch({ type: 'PARSE_INPUT_ACTION', payload: '', component: 'end' });
    },
    valid: () => {
      return state.valid && state.validEnd;
    }
  }));

  const renderInput = () => (
    <InputDateRangePicker
      errorLabelComponent={errorLabelComponent}
      componentId={componentId}
      allowedCharacters={allowedCharacters}
      className={className}
      style={style}
      inputItem={inputItem}
      leftInputRef={leftInputRef}
      rightInputRef={rightInputRef}
      leftComponentReference={leftComponentReference}
      rightComponentReference={rightComponentReference}
      clearButton={clearButton}
      focusClass={focusClass}
      invalidClass={invalidClass}
      labelKey={labelKey}
      departureLabelKey={departureLabelKey}
      returnLabelKey={returnLabelKey}
      leftInput={leftInputItem}
      showLabel={showLabel}
      clearAction={(data: any) => {
        const { showCloseReturnIcon, showCloseDepartureIcon } = data;
        if (showCloseDepartureIcon) {
          dispatch({ type: 'CLEAR_ACTION_DEPARTURE' });
        } else if (showCloseReturnIcon) {
          dispatch({ type: 'CLEAR_ACTION_RETURN' });
        }
      }}
      onBlur={() => {
        if (toggleElement === null || toggleElement === undefined) {
          setAccessibilityState(true);
        }
      }}
      openPopupOnKeyDown={openPopupOnKeyDown}
      openPopupOnClick={openPopupOnClick}
      format={format}
      onChange={handleInputChange}
      onFocus={(event, component) => {
        onFocus(event, component);
        setTimeout(() => {
          isCloseRef.current = false;
        }, 100);
        if (toggleElement === null || toggleElement === undefined) {
          setAccessibilityState(false);
        }
      }}
      onDayClick={handleDayClick}
      iconClose={iconClose}
      rightIcon={rightIcon}
      rightIconOpen={rightIconOpen}
      rightInput={rightInputItem}
      showSeparator={showSeparator}
      textLabel={textLabel}
      isValid={state.valid}
      isValidEnd={state.validEnd}
      isPopopverOpen={state.popoverOpen}
      value={state.inputValue}
      onValueClass={onValueClass}
      dispatch={dispatch}
      state={state}
      modifiers={modifiers}
      component={state.selectedComponent}
      isMobile={isMobile}
      isRequired={isRequired}
      isClearEmptyInput={isClearEmptyInput}
      onClick={(e: any) => {
        if (enabledExperimentalPopover) {
          if (isOpenPopover) {
            overlayState.close();
          } else {
            overlayState.open();
          }
          setIsOpenPopover(!isOpenPopover);
        }
      }}
      onKeyDown={(e: any) => {
        if (enabledExperimentalPopover) {
          if (e.keyCode === 40) {
            if (!isOpenPopover) {
              overlayState.open();
              setIsOpenPopover(!isOpenPopover);
            }
          }
        }
      }}
      buttonDepartureClearId={buttonDepartureClearId}
      buttonDepartureIconId={buttonDepartureIconId}
      buttonReturnIconId={buttonReturnIconId}
      buttonReturnClearId={buttonReturnClearId}
      showCloseDepartureIcon={showClearInputButton && !!state.inputValue?.start?.length}
      showCloseReturnIcon={showClearInputButton && !!state.inputValue?.end?.length}
      childrenLeft={childrenLeft}
      childrenRight={childrenRight}
    />
  );

  const setUserInteraction = (value: boolean) => {
    dispatch({
      type: 'SET_USER_INTERACTION_ACTION',
      payload: value
    });
  };

  const elements: Record<string, any> = {
    InputDate: (
      <InputDateRangePicker
        errorLabelComponent={errorLabelComponent}
        componentId={componentId}
        allowedCharacters={allowedCharacters}
        className={className}
        style={style}
        inputItem={inputItem}
        leftInputRef={leftInputRef}
        rightInputRef={rightInputRef}
        clearButton={clearButton}
        focusClass={focusClass}
        invalidClass={invalidClass}
        labelKey={labelKey}
        departureLabelKey={departureLabelKey}
        returnLabelKey={returnLabelKey}
        leftInput={leftInputItem}
        showLabel={showLabel}
        clearAction={() => {
          dispatch({ type: 'CLEAR_ACTION' });
        }}
        onBlur={() => {
          dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
        }}
        format={format}
        onChange={handleInputChange}
        onFocus={onFocus}
        onDayClick={handleDayClick}
        iconClose={iconClose}
        closePopoverAfterAction={inputItem.closePopoverAfterAction}
        rightIcon={rightIcon}
        rightIconOpen={rightIconOpen}
        rightInput={rightInputItem}
        showSeparator={showSeparator}
        showCloseDepartureIcon={showClearInputButton && !!state.inputValue?.start?.length}
        showCloseReturnIcon={showClearInputButton && !!state.inputValue?.end?.length}
        textLabel={textLabel}
        isValid={state.valid}
        isValidEnd={state.validEnd}
        isPopopverOpen={state.popoverOpen}
        value={state.inputValue}
        onValueClass={onValueClass}
        dispatch={dispatch}
        state={state}
        modifiers={modifiers}
        component={state.selectedComponent}
        buttonDepartureClearId={buttonDepartureClearId}
        buttonDepartureIconId={buttonDepartureIconId}
        buttonReturnIconId={buttonReturnIconId}
        buttonReturnClearId={buttonReturnClearId}
      />
    ),
    CalendarHeader: (
      <CalendarHeader
        {...navigationItem}
        dispatch={dispatch}
        state={state}
        format={format}
        currentMonth={state.currentMonth}
        numberOfMonths={numberOfMonths}
        currentMonthDateA11y={state.currentMonthDateA11y}
        accessibilityState={accessibilityState}
        setPreselectedDate={() => {
          if (state.months.length > 0) {
            if (!state.preSelected) {
              const newDate = departure.start
                ? getAddDays(state.today, departure.start)
                : state.today;
              dispatch({
                type: 'SET_PRESELECTED_DATE',
                payload: state.months[0].firstAvailableDay() ?? newDate
              });
              setTimeout(() => {
                dispatch({
                  type: 'SET_USER_INTERACTION_ACTION',
                  payload: true
                });
              }, 100);
            } else {
              dispatch({
                type: 'SET_PRESELECTED_DATE',
                payload: state.preSelected
              });
            }
            dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
          }
        }}
        maxTripDuration={maxTripDuration}
        maxDate={maxDate}
      />
    ),
    Calendar: (
      <CalendarContent
        isDateRange
        key={'calendar-content-key'}
        numberOfMonths={numberOfMonths}
        showYear={showYearCaption}
        months={state.months}
        captionItem={captionItem}
        weekdayItem={weekdayItem}
        dayItems={dayItems}
        onDayClick={handleDayClick}
        showWeekDayNames={showWeekDayNames}
        weekDaysContainer={weekDaysContainer}
        calendarContainer={calendarContainer}
        calendarMonthContainer={calendarMonthContainer}
        componentName="SingleDateRangePicker"
        // onDayHover={handleDayHover}
        // renderDay={renderDay}
        currentDate={JSON.stringify(state.currentDate) === '{}' ? null : state.currentDate}
        displayDate={state.displayDate}
        dispatch={dispatch}
        dayWidthSetting={dayWidthSetting}
        setUserInteraction={(e: boolean) => {
          setUserInteraction(e);
        }}
      />
    ),
    Footer: (
      <CalendarFooter
        enableFlexibleDates={enableFlexibleDates}
        checkedFlexibleDates={state.isCheckedFlexibleDates}
        flexibleDatesRangeValues={flexibleDatesRangeValues}
        footerCheckboxComponent={footerCheckboxComponent}
        footerSelectComponent={footerSelectComponent}
        footerClearButtonComponent={footerClearButtonComponent}
        footerDoneButtonComponent={footerDoneButtonComponent}
        footerContainerComponent={footerContainerComponent}
        flexibleValue={parseInt('' + state.flexibleValue)}
        dispatch={dispatch}
        state={state}
        footerSettings={footer}
        firstAvailableDay={departure.start ? departure.start : 0}
      />
    )
  };

  const mobileElements: Record<string, any> = {
    InputDate: (
      <InputDateRangePicker
        errorLabelComponent={errorLabelComponent}
        componentId={componentId}
        allowedCharacters={allowedCharacters}
        className={`${className} input-date-range-mobile`}
        style={style}
        inputItem={inputItem}
        leftInputRef={leftInputRef}
        rightInputRef={rightInputRef}
        leftComponentReference={leftComponentReference}
        rightComponentReference={rightComponentReference}
        clearButton={clearButton}
        focusClass={focusClass}
        invalidClass={invalidClass}
        labelKey={labelKey}
        departureLabelKey={departureLabelKey}
        returnLabelKey={returnLabelKey}
        leftInput={leftInputItem}
        showLabel={showLabel}
        clearAction={() => {
          dispatch({ type: 'CLEAR_ACTION' });
        }}
        onBlur={() => {
          // dispatch({ type: 'SET_VALID', payload: true });
          dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
        }}
        format={format}
        onChange={handleInputChange}
        onFocus={(event, component) => {
          onFocus(event, component);
          if (isMobile) {
            if (minTripDuration && minTripDuration > 0 && component === 'end') {
              if (state.selectedRange?.start) {
                dispatch({
                  type: 'MIN_TRIP_DURATION_DAYS',
                  payload: [
                    {
                      start: dayjs(state.today).toDate(),
                      end: dayjs(state.selectedRange?.start)
                        .add(minTripDuration - 1, 'd')
                        .toDate()
                    }
                  ]
                });
              } else {
                dispatch({
                  type: 'MIN_TRIP_DURATION_DAYS',
                  payload: [
                    {
                      start: isDayBefore(dayjs(minDate).toDate(), state.today)
                        ? state.today
                        : dayjs(minDate).toDate(),

                      end: dayjs(
                        isDayBefore(dayjs(minDate).toDate(), state.today)
                          ? state.today
                          : dayjs(minDate).toDate()
                      )
                        .add(minTripDuration - 1, 'd')
                        .toDate()
                    }
                  ]
                });
              }
            } else {
              if (minTripDuration && component === 'start') {
                if (maxDate) {
                  dispatch({
                    type: 'MIN_TRIP_DURATION_DAYS',
                    payload: [
                      {
                        start: dayjs(maxDate)
                          .subtract(minTripDuration - 1, 'd')
                          .toDate(),
                        end: dayjs(maxDate).toDate()
                      }
                    ]
                  });
                } else {
                  dispatch({
                    type: 'MIN_TRIP_DURATION_DAYS',
                    payload: [
                      {
                        start: dayjs()
                          .add(departure.end, 'day')
                          .subtract(minTripDuration, 'd')
                          .toDate(),
                        end: dayjs().add(departure.end, 'day').toDate()
                      }
                    ]
                  });
                }
              } else {
                if (blackoutDays?.length === 0) {
                  dispatch({
                    type: 'MIN_TRIP_DURATION_DAYS',
                    payload: []
                  });
                }
              }
            }
          }
        }}
        onDayClick={handleDayClick}
        iconClose={iconClose}
        closePopoverAfterAction={inputItem.closePopoverAfterAction}
        rightIcon={rightIcon}
        rightIconOpen={rightIconOpen}
        rightInput={rightInputItem}
        showSeparator={showSeparator}
        showCloseDepartureIcon={showClearInputButton && !!state.inputValue?.start?.length}
        showCloseReturnIcon={showClearInputButton && !!state.inputValue?.end?.length}
        textLabel={textLabel}
        isValid={state.valid}
        isValidEnd={state.validEnd}
        isPopopverOpen={state.popoverOpen}
        value={state.inputValue}
        onValueClass={onValueClass}
        dispatch={dispatch}
        state={state}
        modifiers={modifiers}
        component={state.selectedComponent}
        isMobile={true}
        buttonDepartureClearId={buttonDepartureClearId}
        buttonDepartureIconId={buttonDepartureIconId}
        buttonReturnIconId={buttonReturnIconId}
        buttonReturnClearId={buttonReturnClearId}
        isMobileOpen={true}
      />
    ),
    WeekDayNames: (
      <WeekDayNames weekdayItem={weekdayItem} weekDaysContainer={weekDaysContainer}></WeekDayNames>
    ),
    Calendar: (
      <CalendarContent
        isDateRange
        isMobile={true}
        showYear={showYearCaption}
        ref={containerRef}
        numberOfMonths={numberOfMonths}
        months={state.months}
        displayDate={state.displayDate}
        captionItem={captionItem}
        weekdayItem={weekdayItem}
        dayItems={dayItems}
        onDayClick={handleDayClick}
        showWeekDayNames={showWeekDayNames}
        weekDaysContainer={weekDaysContainer}
        calendarContainer={calendarContainer}
        calendarMonthContainer={calendarMonthContainer}
        currentDate={JSON.stringify(state.currentDate) === '{}' ? null : state.currentDate}
        preSelectedDate={state.preSelected}
        dayWidthSetting={dayWidthSetting}
        componentName="SingleDateRangePicker"
      />
    ),
    Footer: (
      <CalendarFooter
        enableFlexibleDates={enableFlexibleDates}
        checkedFlexibleDates={state.isCheckedFlexibleDates}
        flexibleDatesRangeValues={flexibleDatesRangeValues}
        footerCheckboxComponent={footerCheckboxComponent}
        footerSelectComponent={footerSelectComponent}
        footerClearButtonComponent={footerClearButtonComponent}
        footerDoneButtonComponent={footerDoneButtonComponent}
        footerContainerComponent={footerContainerComponent}
        callToAction={callToAction}
        flexibleValue={parseInt('' + state.flexibleValue)}
        dispatch={dispatch}
        state={state}
        footerSettings={footer}
        footerInformationTextComponent={footerInformationTextComponent}
        onSaveFunction={(value: boolean) => {
          return value
            ? flexibleFilterContainerRef?.current?.save()
            : flexibleFilterContainerRef?.current?.dismiss();
        }}
        isMobile={true}
        format={format}
        firstAvailableDay={departure.start ? departure.start : 0}
      />
    )
  };

  //refs
  const triggerRef = useRef(null);
  let overlayState = useOverlayTriggerState({
    onOpenChange: (isOpen) => {
      if (isOpen) {
        handleOpenPopover();
      } else {
        handleClosePopover();
      }
    }
  });
  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps, updatePosition } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: popoverRef,
    placement: 'bottom left',
    offset: 0,
    isOpen: overlayState.isOpen
  });
  let overlayTriggerState1 = { ...overlayState, close: updatePosition };

  let { triggerProps } = useOverlayTrigger({ type: 'dialog' }, overlayTriggerState1, triggerRef);

  // console.log('triggerProps', triggerProps);
  // useButton ensures that focus management is handled correctly,
  // across all browsers. Focus is restored to the button once the
  // popover closes.
  let { buttonProps } = useButton(
    {
      onPress: (e) => {
        overlayState.open();
      },
      elementType: 'div'
    },
    triggerRef
  );

  const memoState = useMemo(
    () => ({
      ...state,
      popoverOpen: overlayState.isOpen
    }),
    [state, overlayState.isOpen]
  );

  const temporalAvailableAtoms = [...availableAtoms];
  const getTopAtom = () =>
    temporalAvailableAtoms.filter((e) => setTopAtoms?.find((element) => element === e));
  const removeTopAtom = () => temporalAvailableAtoms.filter((e) => setTopAtoms?.indexOf(e) === -1);
  const footerPosition = temporalAvailableAtoms.indexOf('Footer');
  const newAvailableAtoms =
    footerPosition !== -1
      ? temporalAvailableAtoms.splice(footerPosition, 1)
      : temporalAvailableAtoms;

  const { handleClickOutPopup } = useGetHandleClickOutPopup({
    isActiveFooter: footerPosition !== -1
  });

  const newToggleElement = React.cloneElement(toggleElement ?? renderInput(), {
    ...buttonProps,
    ...triggerProps,
    ...(toggleElement ?? renderInput()).props,
    ref: triggerRef,
    leftComponentReference: triggerRef
  });

  const [popoverStyle] = useStyles(popover, {
    direction,
    theme
  });

  useEffect(() => {
    if (enabledExperimentalPopover) {
      if (!overlayState.isOpen) {
        if (changePopoverState) {
          changePopoverState?.(overlayState.isOpen);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayState.isOpen]);

  let newPositionProps: any = { ...positionProps };
  if (newPositionProps) {
    delete newPositionProps['style']['maxHeight'];
  }

  return isMobile ? (
    <ThemeProvider theme={theme?.designTokens ?? undefined}>
      <CalendarProvider
        modifiers={modifiers}
        modifiersColors={modifiersColors}
        state={state}
        dispatch={dispatch}
        format={format}
        numberOfMonths={numberOfMonths}
      >
        <FlexibleFilterContainer
          toggleElement={toggleElement ?? renderInput()}
          ref={flexibleFilterContainerRef}
          title={translate('cmp-date-range-picker-header-title', 'Date range')}
          variant="layout.popover"
          label={'label'}
          onSave={() => {
            if (Object.keys(state.selectedRange).length > 0) {
              if (state.selectedRange.start) {
                dispatch({
                  type: 'SET_DATE_ACTION',
                  payload: state.selectedRange.start,
                  component: 'start',
                  close: true
                });
              }
              if (state.selectedRange.end) {
                dispatch({
                  type: 'SET_DATE_ACTION',
                  payload: state.selectedRange.end,
                  component: 'end',
                  close: true
                });
              }
            }
          }}
          onDismiss={() => {
            dispatch({ type: 'CHANGE_ACTION' });
            changePopoverState?.(false);
          }}
          onScroll={() => {}}
          footer={footer.elementsToShow ? mobileElements['Footer'] : null}
          mobileView={isMobile}
        >
          {setTopAtoms && (
            <Box
              position="absolute"
              bgColor="white"
              style={{
                left: 0,
                right: 0,
                zIndex: 100
              }}
              {...topAtomsContainer}
            >
              {getTopAtom().map((e: any) => mobileElements[e])}
            </Box>
          )}
          {setTopAtoms
            ? removeTopAtom().map((e) => mobileElements[e])
            : availableAtoms && newAvailableAtoms.map((e) => mobileElements[e])}
        </FlexibleFilterContainer>
      </CalendarProvider>
    </ThemeProvider>
  ) : (
    <>
      {enabledExperimentalPopover ? (
        <ThemeProvider theme={theme.designTokens ?? undefined}>
          <CalendarProvider
            modifiers={modifiers}
            modifiersColors={modifiersColors}
            state={memoState}
            dispatch={dispatch}
            format={format}
            numberOfMonths={numberOfMonths}
          >
            {newToggleElement}
            {overlayState.isOpen ? (
              <OverlayContainer>
                <Popover
                  {...Object.assign({ sx: { bg: 'white' } }, { ...positionProps })}
                  className={`${popoverStyle} fr-popover-direction`}
                  aria-label={translate(
                    datePickerDialogAriaLabel,
                    'Departure/Return date calendar'
                  )}
                  popoverRef={popoverRef}
                  isOpen={overlayState.isOpen}
                  onClose={() => {
                    handleClosePopover();
                    overlayState.close();
                    isCloseRef.current = true;
                  }}
                  onClickOutPopup={() => {
                    dispatch({ type: 'CHANGE_ACTION' });
                  }}
                >
                  {availableAtoms && availableAtoms.map((e) => elements[e])}
                </Popover>
              </OverlayContainer>
            ) : null}
          </CalendarProvider>
        </ThemeProvider>
      ) : (
        <CalendarProvider
          modifiers={modifiers}
          modifiersColors={modifiersColors}
          state={state}
          dispatch={dispatch}
          format={format}
          numberOfMonths={numberOfMonths}
        >
          <PopoverOld
            alignLeft={true}
            element={toggleElement ?? <>{renderInput()}</>}
            ref={popoverRef}
            onClose={handleClosePopover}
            onOpen={handleOpenPopover}
            onClickOutPopup={() => {
              dispatch({ type: 'CHANGE_ACTION' });
            }}
            minWidth="unset"
            dialogAriaLabel={translate(datePickerDialogAriaLabel, 'Departure/Return date calendar')}
            {...popover}
          >
            <Calendar {...calendarProps}>
              {availableAtoms && availableAtoms.map((e) => elements[e])}
            </Calendar>
          </PopoverOld>
        </CalendarProvider>
      )}
    </>
  );
}

SingleDateRangePicker.componentName = 'SingleDateRangePicker';

export default forwardRef(SingleDateRangePicker);
